import { createDeck, shuffle } from './cards.js';
import { evaluateHand, winners } from './evaluator.js';
import { PERSONALITIES } from './ai.js';
import { RAILROAD_UNLOCK, TABLE_MODES, addCurrency, allocateCurrency, inventoryValue, withdrawCurrency } from './currency.js';

export const PHASE = {
  IDLE: 'IDLE',
  BET_PRE: 'BET_PRE',
  BET_FLOP: 'BET_FLOP',
  BET_TURN: 'BET_TURN',
  BET_RIVER: 'BET_RIVER',
  SHOWDOWN: 'SHOWDOWN',
  GAME_OVER: 'GAME_OVER'
};

export const BUY_IN = TABLE_MODES.frontier.buyIn;
export const ANTE = TABLE_MODES.frontier.ante;
export const PRE_DRAW_BET = TABLE_MODES.frontier.preDrawBet;
export const POST_DRAW_BET = TABLE_MODES.frontier.postDrawBet;
export const HARD_TIMES_BALANCE = TABLE_MODES.frontier.hardTimesBalance;
export const ROBBERY_THRESHOLD = 500000;
export const ROBBERY_TAKE = 100000;

export function formatMoney(cents) {
  return `$${(Math.max(0, cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function patronBuyIn(playerStack, economy, random = Math.random) {
  const unit = Math.max(1, Math.round(economy?.ante || 1));
  const target = Math.max(unit, Math.round(Number(playerStack) || 0));
  const sampled = Number(random());
  const roll = Number.isFinite(sampled) ? Math.min(1, Math.max(0, sampled)) : 0.5;
  const hardTimes = target < Math.max(unit, Math.round(economy?.hardTimesBalance || 0));
  const spread = hardTimes ? 0.1 : economy?.id === TABLE_MODES.baron.id ? 0.25 : 0.2;
  const variedTarget = target * (1 - spread + roll * spread * 2);
  return Math.max(unit, Math.round(variedTarget / unit) * unit);
}

function createSeat(id, name, aiIndex = null, buyIn = BUY_IN, currencyProfile = 'balanced') {
  return {
    id, name, aiIndex, stack: buyIn, cards: [], folded: false, sittingOut: false, allIn: false,
    roundBet: 0, handBet: 0, roundCurrency: {}, purse: id === 0 ? allocateCurrency(buyIn, currencyProfile) : null,
    currencyProfile, discardCount: null, lastAction: ''
  };
}

export function chooseStartingPatrons(random = Math.random) {
  const order = PERSONALITIES.map((_, index) => index);
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [order[index], order[swap]] = [order[swap], order[index]];
  }
  return order.slice(0, 3);
}

export function createGame(indexer, patronRandom = Math.random, career = {}) {
  const saved = career && typeof career === 'object' ? career : {};
  const economy = saved.railroadUnlocked ? TABLE_MODES.baron : TABLE_MODES.frontier;
  const playerStack = Number.isFinite(saved.stack) && saved.stack >= 0 ? Math.round(saved.stack) : TABLE_MODES.frontier.buyIn;
  const currencyProfile = saved.currencyProfile && ['balanced', 'paper', 'gold', 'silver'].includes(saved.currencyProfile) ? saved.currencyProfile : 'balanced';
  const startingPatrons = chooseStartingPatrons(patronRandom);
  const seats = [createSeat(0, 'You', null, playerStack, currencyProfile), ...startingPatrons.map((aiIndex, index) => createSeat(index + 1, PERSONALITIES[aiIndex].name, aiIndex, economy.buyIn))];
  return {
    version: 3,
    handNumber: 0,
    dealer: 3,
    phase: PHASE.IDLE,
    seats,
    deck: [],
    deckIndex: 0,
    communityCards: [],
    burnCards: [],
    currentBet: 0,
    betUnit: economy.preDrawBet,
    raises: 0,
    pending: [],
    actingSeat: null,
    pot: 0,
    potCurrency: {},
    message: 'The table is waiting.',
    result: null,
    tableMode: economy.id,
    buyIn: economy.buyIn,
    ante: economy.ante,
    preDrawBet: economy.preDrawBet,
    postDrawBet: economy.postDrawBet,
    hardTimesBalance: economy.hardTimesBalance,
    extremePot: economy.extremePot,
    hardTimes: false,
    railroadUnlocked: Boolean(saved.railroadUnlocked),
    promotionPending: false,
    promotion: null,
    peakBankroll: Math.max(playerStack, Math.round(saved.peakBankroll || 0)),
    robberyOccurred: Boolean(saved.robberyOccurred),
    robbery: null,
    tableChips: seats.reduce((sum, seat) => sum + seat.stack, 0),
    nextPatron: (startingPatrons.at(-1) + 1) % PERSONALITIES.length,
    turnover: null,
    indexer,
    revision: 0
  };
}

const active = state => state.seats.filter(seat => !seat.folded && !seat.allIn && seat.stack > 0);
const contenders = state => state.seats.filter(seat => !seat.folded && seat.cards.length === 2);

function nextFrom(state, from, predicate) {
  for (let offset = 1; offset <= state.seats.length; offset += 1) {
    const index = (from + offset) % state.seats.length;
    if (predicate(state.seats[index])) return index;
  }
  return null;
}

function pay(state, seat, amount) {
  const paid = Math.min(seat.stack, Math.max(0, amount));
  seat.stack -= paid;
  seat.roundBet += paid;
  seat.handBet += paid;
  state.pot += paid;
  if (seat.id === 0) {
    if (inventoryValue(seat.purse) !== seat.stack + paid) seat.purse = allocateCurrency(seat.stack + paid, seat.currencyProfile);
    const withdrawal = withdrawCurrency(seat.purse, paid, seat.currencyProfile);
    seat.purse = withdrawal.remaining;
    seat.roundCurrency = addCurrency(seat.roundCurrency, withdrawal.tender);
    state.potCurrency = addCurrency(state.potCurrency, withdrawal.tender);
  } else {
    state.potCurrency = addCurrency(state.potCurrency, allocateCurrency(paid));
  }
  if (seat.stack === 0) seat.allIn = true;
  return paid;
}

function dealCard(state) {
  const card = state.deck[state.deckIndex];
  if (!card) throw new Error('Deck exhausted');
  state.deckIndex += 1;
  return card;
}

function resetBetting(state, phase) {
  state.phase = phase;
  state.currentBet = 0;
  state.raises = 0;
  state.betUnit = phase === PHASE.BET_PRE ? state.preDrawBet : state.postDrawBet;
  state.seats.forEach(seat => { seat.roundBet = 0; seat.roundCurrency = {}; seat.lastAction = ''; });
  const bettingSeats = active(state);
  state.pending = bettingSeats.length <= 1 && contenders(state).length > 1 ? [] : bettingSeats.map(seat => seat.id);
  state.actingSeat = nextFrom(state, state.dealer, seat => state.pending.includes(seat.id));
}

export function startHand(state, tableRandom = Math.random) {
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  next.turnover = null;
  next.robbery = null;
  next.promotion = null;
  if (next.railroadUnlocked && next.tableMode !== TABLE_MODES.baron.id) {
    const economy = TABLE_MODES.baron;
    next.seats.slice(1).forEach(seat => {
      next.tableChips += economy.buyIn - seat.stack;
      seat.stack = economy.buyIn;
    });
    Object.assign(next, {
      tableMode: economy.id, buyIn: economy.buyIn, ante: economy.ante,
      preDrawBet: economy.preDrawBet, postDrawBet: economy.postDrawBet,
      hardTimesBalance: economy.hardTimesBalance, extremePot: economy.extremePot,
      promotionPending: false,
      promotion: { title: 'Railroad Baron stakes reached', detail: `${formatMoney(economy.ante)} ante · thousand-dollar pots` }
    });
  }
  if (next.seats[0].stack < next.ante) {
    next.phase = PHASE.GAME_OVER;
    next.message = 'Your purse cannot cover the ante. Start a new game to play again.';
    return next;
  }
  if (state.phase === PHASE.SHOWDOWN && state.result) {
    const occupied = new Set(next.seats.slice(1).map(seat => seat.aiIndex));
    const leaving = [];
    next.seats.slice(1).forEach(seat => {
      if (seat.sittingOut) return;
      const won = state.result.winners.includes(seat.id);
      const playerStack = next.seats[0].stack;
      const stackRatio = playerStack > 0 ? seat.stack / playerStack : 1;
      const mismatchedBankroll = stackRatio < 0.45 || stackRatio > 2.2;
      const chance = seat.stack < next.ante ? 1 : won ? 0.07 : mismatchedBankroll ? 0.36 : seat.stack < next.buyIn * 0.44 ? 0.48 : 0.16;
      if (tableRandom() >= chance) return;
      let candidate = next.nextPatron;
      for (let tries = 0; tries < PERSONALITIES.length; tries += 1) {
        candidate %= PERSONALITIES.length;
        if (!occupied.has(candidate)) break;
        candidate += 1;
      }
      occupied.delete(seat.aiIndex);
      occupied.add(candidate);
      const joiningStack = patronBuyIn(playerStack, next, tableRandom);
      leaving.push({ from: seat.name, to: PERSONALITIES[candidate].name, angry: !won, buyIn: joiningStack });
      next.tableChips += joiningStack - seat.stack;
      seat.aiIndex = candidate;
      seat.name = PERSONALITIES[candidate].name;
      seat.stack = joiningStack;
      seat.lastAction = 'TAKES A SEAT';
      next.nextPatron = (candidate + 1) % PERSONALITIES.length;
    });
    if (leaving.length) next.turnover = leaving;
  }
  next.hardTimes = next.seats[0].stack < next.hardTimesBalance;
  const playerStack = next.seats[0].stack;
  const availableOpponents = next.seats.slice(1)
    .filter(seat => seat.stack >= next.ante)
    .sort((a, b) => Math.abs(a.stack - playerStack) - Math.abs(b.stack - playerStack) || a.id - b.id);
  const hardTimesOpponent = availableOpponents[0]?.id ?? null;
  next.seats.slice(1).forEach(seat => { seat.sittingOut = next.hardTimes && seat.id !== hardTimesOpponent; });
  next.handNumber += 1;
  next.dealer = nextFrom(next, next.dealer, seat => seat.stack > 0 && !seat.sittingOut);
  next.deck = shuffle(createDeck(), state.indexer);
  next.deckIndex = 0;
  next.communityCards = [];
  next.burnCards = [];
  next.pot = 0;
  next.potCurrency = {};
  next.result = null;
  next.seats.forEach(seat => {
    seat.cards = [];
    seat.folded = seat.stack < next.ante || seat.sittingOut;
    seat.allIn = false;
    seat.roundBet = 0;
    seat.handBet = 0;
    seat.roundCurrency = {};
    seat.discardCount = null;
    seat.lastAction = seat.folded ? 'OUT' : '';
    if (!seat.folded) pay(next, seat, next.ante);
  });
  for (let card = 0; card < 2; card += 1) {
    for (let offset = 1; offset <= next.seats.length; offset += 1) {
      const index = (next.dealer + offset) % next.seats.length;
      if (!next.seats[index].folded) next.seats[index].cards.push(dealCard(next));
    }
  }
  resetBetting(next, PHASE.BET_PRE);
  if (!next.pending.length) return advancePhase(next);
  const opener = next.seats[next.actingSeat];
  next.message = next.hardTimes
    ? `Hard times. The room clears for heads-up play. ${formatMoney(next.ante)} ante in.`
    : next.promotion
      ? `${next.promotion.title}. ${formatMoney(next.ante)} ante in.`
      : `Hand ${next.handNumber}. ${formatMoney(next.ante)} ante in. ${opener.id === 0 ? 'You act' : `${opener.name} acts`} first.`;
  next.revision += 1;
  return next;
}

export function getLegalActions(state, seatId = state.actingSeat) {
  if (![PHASE.BET_PRE, PHASE.BET_FLOP, PHASE.BET_TURN, PHASE.BET_RIVER].includes(state.phase) || seatId !== state.actingSeat) return [];
  const seat = state.seats[seatId];
  if (!seat || seat.folded || seat.allIn) return [];
  const toCall = Math.max(0, state.currentBet - seat.roundBet);
  const actions = [{ type: 'FOLD', label: 'Fold' }];
  if (toCall === 0) actions.push({ type: 'CHECK', label: 'Check', amount: 0 });
  else if (toCall < seat.stack) actions.push({ type: 'CALL', label: `Call ${formatMoney(toCall)}`, amount: toCall });
  if (state.raises < 3 && seat.stack > toCall) {
    const amount = Math.min(toCall + state.betUnit, seat.stack);
    if (amount < seat.stack) actions.push({ type: state.currentBet === 0 ? 'BET' : 'RAISE', label: state.currentBet === 0 ? `Bet ${formatMoney(amount)}` : `Raise ${formatMoney(amount)}`, amount });
  }
  if (seat.stack > 0) actions.push({
    type: 'ALL_IN',
    label: `All in ${formatMoney(seat.stack)}`,
    amount: seat.stack,
    intent: toCall >= seat.stack ? 'CALL' : 'RAISE'
  });
  return actions;
}

function advanceActor(state, after) {
  if (contenders(state).length === 1) return settleUncontested(state);
  if (state.pending.length === 0) return advancePhase(state);
  state.actingSeat = nextFrom(state, after, seat => state.pending.includes(seat.id) && !seat.folded && !seat.allIn);
  if (state.actingSeat === null) return advancePhase(state);
  const actor = state.seats[state.actingSeat];
  state.message = actor.id === 0 ? 'You are considering the wager.' : `${actor.name} is considering the wager.`;
  return state;
}

export function act(state, seatId, action) {
  const legal = getLegalActions(state, seatId);
  const legalAction = legal.find(item => item.type === action.type);
  if (!legalAction) throw new Error(`Illegal action ${action.type}`);
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  const seat = next.seats[seatId];
  if (action.type === 'FOLD') {
    seat.folded = true;
    seat.lastAction = 'FOLDS';
  } else if (action.type === 'CHECK') {
    seat.lastAction = 'CHECKS';
  } else if (action.type === 'CALL') {
    const due = next.currentBet - seat.roundBet;
    const paid = pay(next, seat, due);
    seat.lastAction = paid < due ? `ALL-IN ${formatMoney(paid)}` : `CALLS ${formatMoney(paid)}`;
  } else if (action.type === 'ALL_IN') {
    const previousBet = next.currentBet;
    const paid = pay(next, seat, seat.stack);
    const raised = seat.roundBet > previousBet;
    if (raised) {
      next.currentBet = seat.roundBet;
      next.raises += 1;
      next.pending = active(next).filter(other => other.id !== seatId).map(other => other.id);
    }
    seat.lastAction = `ALL IN ${formatMoney(paid)}`;
  } else {
    const paid = pay(next, seat, (next.currentBet - seat.roundBet) + next.betUnit);
    next.currentBet = Math.max(next.currentBet, seat.roundBet);
    next.raises += 1;
    seat.lastAction = `${action.type === 'BET' ? 'BETS' : 'RAISES'} ${formatMoney(paid)}`;
    next.pending = active(next).filter(other => other.id !== seatId).map(other => other.id);
  }
  next.pending = next.pending.filter(id => id !== seatId && !next.seats[id].folded && !next.seats[id].allIn);
  next.revision += 1;
  return advanceActor(next, seatId);
}

function revealStreet(state, phase, count, streetName) {
  state.burnCards.push(dealCard(state));
  for (let card = 0; card < count; card += 1) state.communityCards.push(dealCard(state));
  resetBetting(state, phase);
  state.revision += 1;
  if (!state.pending.length) return advancePhase(state);
  const opener = state.seats[state.actingSeat];
  state.message = `${streetName}. ${opener.id === 0 ? 'You act' : `${opener.name} acts`} first.`;
  return state;
}

function advancePhase(state) {
  if (state.phase === PHASE.BET_PRE) return revealStreet(state, PHASE.BET_FLOP, 3, 'The flop is dealt');
  if (state.phase === PHASE.BET_FLOP) return revealStreet(state, PHASE.BET_TURN, 1, 'The turn is dealt');
  if (state.phase === PHASE.BET_TURN) return revealStreet(state, PHASE.BET_RIVER, 1, 'The river is dealt');
  if (state.phase === PHASE.BET_RIVER) return settleShowdown(state);
  return state;
}

function settleUncontested(state) {
  const winner = contenders(state)[0];
  const award = state.pot;
  winner.stack += state.pot;
  if (winner.id === 0) winner.purse = allocateCurrency(winner.stack, winner.currencyProfile);
  state.result = { winners: [winner.id], title: winner.id === 0 ? 'You take the pot' : `${winner.name} takes the pot`, detail: `${formatMoney(award)} — everyone else folded.` };
  state.pot = 0;
  state.potCurrency = {};
  state.phase = PHASE.SHOWDOWN;
  state.actingSeat = null;
  state.message = state.result.title;
  return triggerRobberyIfNeeded(trackProgression(state));
}

function settleShowdown(state) {
  const award = state.pot;
  const eligibleSeats = contenders(state);
  if (state.communityCards.length !== 5) throw new Error('Showdown requires a complete five-card board');
  const showdown = eligibleSeats.map(seat => ({
    id: seat.id,
    cards: [...seat.cards, ...state.communityCards],
    evaluation: evaluateHand([...seat.cards, ...state.communityCards])
  }));
  const contributionLevels = [...new Set(state.seats.map(seat => seat.handBet).filter(Boolean))].sort((a, b) => a - b);
  const awarded = new Set();
  let previous = 0;

  contributionLevels.forEach(level => {
    const funders = state.seats.filter(seat => seat.handBet >= level);
    const layer = (level - previous) * funders.length;
    const eligible = eligibleSeats.filter(seat => seat.handBet >= level);
    if (!eligible.length || layer <= 0) return;
    const best = winners(eligible.map(seat => ({ id: seat.id, cards: [...seat.cards, ...state.communityCards] })));
    const ordered = [...best].sort((a, b) => ((a.id - state.dealer + state.seats.length) % state.seats.length) - ((b.id - state.dealer + state.seats.length) % state.seats.length));
    const share = Math.floor(layer / ordered.length);
    let remainder = layer - share * ordered.length;
    ordered.forEach(hand => {
      state.seats[hand.id].stack += share + (remainder > 0 ? 1 : 0);
      remainder = Math.max(0, remainder - 1);
      awarded.add(hand.id);
    });
    previous = level;
  });

  const winningSeats = [...awarded].map(id => state.seats[id]);
  const lead = winningSeats[0];
  const evaluation = evaluateHand([...lead.cards, ...state.communityCards]);
  const winnerNames = winningSeats.map(seat => seat.name).join(' & ');
  const title = winningSeats.length === 1 && winningSeats[0].id === 0 ? 'You win the pot' : `${winnerNames} ${winningSeats.length > 1 ? 'share' : 'wins'} the pot`;
  state.result = {
    winners: winningSeats.map(seat => seat.id),
    title,
    detail: `${evaluation.name} — ${formatMoney(award)}.`,
    showdown: showdown.map(hand => ({
      id: hand.id,
      category: hand.evaluation.category,
      name: hand.evaluation.name,
      bestFive: hand.evaluation.bestFive
    }))
  };
  state.pot = 0;
  state.potCurrency = {};
  state.seats[0].purse = allocateCurrency(state.seats[0].stack, state.seats[0].currencyProfile);
  state.phase = PHASE.SHOWDOWN;
  state.actingSeat = null;
  state.message = state.result.title;
  return triggerRobberyIfNeeded(trackProgression(state));
}

export function trackProgression(state) {
  const bankroll = state.seats[0].stack;
  state.peakBankroll = Math.max(state.peakBankroll || 0, bankroll);
  if (!state.railroadUnlocked && state.peakBankroll >= RAILROAD_UNLOCK) {
    state.railroadUnlocked = true;
    state.promotionPending = true;
    if (state.result) state.result.detail += ` Railroad Baron stakes are now unlocked for the next hand.`;
  }
  return state;
}

export function triggerRobberyIfNeeded(state) {
  const player = state.seats[0];
  if (inventoryValue(player.purse) !== player.stack) player.purse = allocateCurrency(player.stack, player.currencyProfile);
  if (state.robberyOccurred || player.stack < ROBBERY_THRESHOLD) return state;
  const taken = Math.min(ROBBERY_TAKE, player.stack);
  player.stack -= taken;
  player.purse = allocateCurrency(player.stack, player.currencyProfile);
  state.tableChips -= taken;
  state.robberyOccurred = true;
  state.robbery = { amount: taken, threshold: ROBBERY_THRESHOLD, remaining: player.stack };
  state.message = `The saloon is robbed! Masked outlaws take ${formatMoney(taken)} from your purse.`;
  return state;
}

export function assertIntegrity(state) {
  const cards = [...state.seats.flatMap(seat => seat.cards), ...(state.communityCards || []), ...(state.burnCards || [])];
  if (new Set(cards).size !== cards.length) throw new Error('Duplicate card in play');
  if (state.seats.some(seat => seat.stack < 0)) throw new Error('Negative stack');
  const total = state.seats.reduce((sum, seat) => sum + seat.stack, 0) + state.pot;
  if (state.phase !== PHASE.IDLE && total !== state.tableChips) throw new Error(`Chip conservation failed: ${total}/${state.tableChips}`);
  if (inventoryValue(state.seats[0].purse) !== state.seats[0].stack) throw new Error('Player purse does not match bankroll');
  if (inventoryValue(state.potCurrency) !== state.pot) throw new Error('Pot currency does not match pot');
  return true;
}

export function careerSnapshot(state) {
  return {
    stack: state.seats[0].stack,
    currencyProfile: state.seats[0].currencyProfile,
    railroadUnlocked: Boolean(state.railroadUnlocked),
    peakBankroll: state.peakBankroll || state.seats[0].stack,
    robberyOccurred: Boolean(state.robberyOccurred)
  };
}
